"use client";

import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Target } from "lucide-react";
import { analyzeRepPatterns } from "@/server-actions/reports/pattern-analysis";

export default function PatternSummaryWidget({ userId, workspaceId, timeframeDays, dateRange }) {
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // dateRange carries either { timeframeDays } or { startDate, endDate, timezone }
  const range = dateRange || { timeframeDays };

  const handleAnalyze = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await analyzeRepPatterns({ userId, workspaceId, ...range });
      if (result.success) {
        setPatterns(result.patterns);
      } else {
        setError(result.error || "Failed to analyze patterns");
      }
    } catch (err) {
      console.error("Pattern analysis error:", err);
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [userId, workspaceId, timeframeDays, dateRange]);

  if (!patterns && !loading && !error) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleAnalyze}
        className="border-premium-200 text-premium-700 hover:bg-premium-50 hover:text-premium-800"
      >
        Analyze Patterns
      </Button>
    );
  }

  if (loading) {
    return (
      <div className="w-full flex items-center gap-2 text-sm text-gray-500 py-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Analyzing cross-session patterns...
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full text-sm text-red-600 py-1">
        {error}
        <button
          onClick={handleAnalyze}
          className="ml-2 text-action-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!patterns) return null;

  return (
    <div className="w-full bg-gradient-to-r from-premium-50 to-action-50 border border-premium-200 rounded-lg p-4 space-y-4">
      <h4 className="text-sm font-semibold text-premium-800">Cross-Session Pattern Analysis</h4>

      {/* Top Priority */}
      {patterns.topPriority && (
        <div className="bg-warning-50 border border-warning-200 rounded-md p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Target className="w-3.5 h-3.5 text-warning-600" />
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Top Priority</span>
          </div>
          <p className="text-sm font-medium text-gray-900">{patterns.topPriority.area}</p>
          <p className="text-xs text-gray-600 mt-1">{patterns.topPriority.reason}</p>
          <p className="text-xs text-gray-800 font-medium mt-1.5">{patterns.topPriority.actionItem}</p>
        </div>
      )}

      {/* Recurring Strengths */}
      {patterns.recurringStrengths?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp className="w-3.5 h-3.5 text-action-600" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recurring Strengths</span>
          </div>
          <div className="space-y-1.5">
            {patterns.recurringStrengths.map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <Badge className="bg-action-100 text-action-700 border-0 text-xs flex-shrink-0 mt-0.5">
                  {s.frequency}
                </Badge>
                <span className="text-sm text-gray-700">{s.pattern}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recurring Weaknesses */}
      {patterns.recurringWeaknesses?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingDown className="w-3.5 h-3.5 text-action-500" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recurring Weaknesses</span>
          </div>
          <div className="space-y-2">
            {patterns.recurringWeaknesses.map((w, i) => (
              <div key={i} className="text-sm">
                <div className="flex items-start gap-2">
                  <Badge className="bg-action-100 text-action-700 border-0 text-xs flex-shrink-0 mt-0.5">
                    {w.frequency}
                  </Badge>
                  <span className="text-gray-700">{w.pattern}</span>
                </div>
                {w.suggestion && (
                  <p className="text-xs text-premium-700 mt-1 ml-2 pl-2 border-l-2 border-premium-200">
                    {w.suggestion}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Behavioral Trends */}
      {patterns.behavioralTrends?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Behavioral Trends</span>
          <ul className="mt-1.5 space-y-1">
            {patterns.behavioralTrends.map((trend, i) => (
              <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                <span className="text-premium-400 mt-0.5">-</span>
                {trend}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
